import { ChangeDetectionStrategy, Component, ChangeDetectorRef, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { Observable } from 'rxjs';
import { CampaignCriteria } from 'src/app/core/models/Campaign_Criterias';
import { CampaignService } from 'src/app/services/campaigns/campaign.service';
import { ProspectsService } from 'src/app/services/prospects/prospects.service';
import { NO_RESULTS_PROSPECTS, PROSPECT_COLUMNS, STATES } from 'src/app/shared/constants';
import Swal from 'sweetalert2';

@Component({
	selector: 'app-prospects',
	templateUrl: './prospects.component.html',
	styleUrls: ['./prospects.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProspectsComponent {
	@ViewChild('prospectsTable') prospectsTable: DatatableComponent;

	loading = false;
	prospects = NO_RESULTS_PROSPECTS;
	ColumnMode = ColumnMode;
	selectedCampaign: number = 0;
	campaignToAssign: number = 0;
	campaigns: any;
	campaignsModal: any;
	columns = PROSPECT_COLUMNS;
	searchValue = '';

	states = STATES;

	public criterias: CampaignCriteria;
	public formCriteria: FormGroup;

	campaigns$: Observable<any[]>;
	selectedStates = [];



	// Essential columns for initial display
	essentialColumns = this.columns.slice(0, 11);

	constructor(private prospectSrv: ProspectsService, private campaignsSrv: CampaignService, private cdr: ChangeDetectorRef, private modalSrv: NgbModal) {
		this.getCampaigns();
		this.criterias = {
			asset_size_max: 0,
			asset_size_min: 0,
			employee_size_max: 0,
			employee_size_min: 0,
			state: '',
			zip_code: '',
			radius: 0,
			plan_participants_min: 0,
			plan_participants_max: 0,
			prospects_from: 'LMS',
			section_employee_size: false,
			section_asset_size: false,
			section_plan_participants: false,
			section_state: false,
			section_zip_code: false,
		};
		this.formCriteria = new FormGroup({
					asset_size_max: new FormControl(''),
					asset_size_min: new FormControl(''),
					employee_size_max: new FormControl(''),
					employee_size_min: new FormControl(''),
					plan_participants_max: new FormControl(''),
					plan_participants_min: new FormControl(''),
					state: new FormControl(''),
					zip_code: new FormControl(''),
					radius: new FormControl(''),
				});
	}

	ngOnInit(): void {
	}

	getCampaigns() {
		this.campaignsSrv.getCampaignsForSelect().subscribe((response: any) => {
			this.campaignsModal = [...response.body];
			this.campaigns = [{ id: 0, name: 'LMS database' }];
			this.campaigns = this.campaigns.concat(response.body);
			this.cdr.detectChanges();
		});
	}

	toggleColumn(column: string) {
		if (this.columns.find((col) => col.prop === column)) {
			this.columns.find((col) => col.prop === column).visible = !this.columns.find((col) => col.prop === column).visible;
		}
	}

	isShowed(column: string) {
		if (this.columns.find((col) => col.prop === column)) {
			return this.columns.find((col) => col.prop === column).visible;
		} else {
			return false;
		}
	}

	updateFilter(event?: any, filterBy?: string) {
		console.info('Filtrando');
	}

	exportToExcel() {
		console.info('Exportando');
	}

	formatCurrency(value: number): string {
		if (value === null || value === undefined) return '-';
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	}

	formatDate(date: string): string {
		if (!date) return '-';
		return new Date(date).toLocaleDateString();
	}

	formatBoolean(value: number): string {
		return value === 1 ? 'Yes' : 'No';
	}

	getProspects() {
		this.loading = true;
		this.prospectSrv.getProspects(this.selectedCampaign).subscribe((response: any) => {
			this.prospects = response.body;
			this.loading = false;
			this.cdr.detectChanges();
		});
	}

	searchProspects() {
		this.loading = true;
		this.prospects = NO_RESULTS_PROSPECTS;
		this.searchValue = this.transformCriteriaToString(this.criterias);
		this.prospectSrv.searchProspects(this.selectedCampaign, this.searchValue, this.criterias.prospects_from).subscribe((response: any) => {
			this.prospects = response.body;
			this.loading = false;
			this.cdr.detectChanges();
			this.prospectsTable.recalculate();
		});
	}

	assignToCampaign() {
		this.loading = true;
		const prospect_ids = this.prospects.map(prospect => prospect.id).join(',');

		this.prospectSrv.assignToCampaign(this.campaignToAssign, prospect_ids).subscribe((response: any) => {
			this.selectedCampaign = this.campaignToAssign;
			this.searchValue = '';
			this.searchProspects();
			this.modalSrv.dismissAll();
			this.loading = false;
			this.cdr.detectChanges();
			this.prospectsTable.recalculate();
			Swal.fire({
				icon: 'success',
				title: 'Assigned!',
				text: 'Prospects have been assigned to the campaign.',
			});
		});
	}

	open(content: TemplateRef<any>) {
		this.modalSrv.open(content, { ariaLabelledBy: 'modal-basic-title', centered: true }).result.then(
			(result) => {
				console.info(result);
			},
			(reason) => {
				console.info(reason);
			}
		);
	}

	unassignFromCampaign() {
		this.loading = true;
		this.prospectSrv.unassignFromCampaign(this.selectedCampaign, this.searchValue).subscribe((response: any) => {
			this.loading = false;
			this.searchProspects();
			this.modalSrv.dismissAll();
			Swal.fire({
				icon: 'success',
				title: 'Unassigned!',
				text: 'Prospects have been unassigned from the campaign.',
			});
			this.prospectsTable.recalculate();
		});
	}

	setCampaign(campaign: any){
		this.criterias = {
			asset_size_max: 0,
			asset_size_min: 0,
			employee_size_max: 0,
			employee_size_min: 0,
			state: '',
			zip_code: '',
			radius: 0,
			plan_participants_min: 0,
			plan_participants_max: 0,
			prospects_from: 'LMS',
			section_employee_size: false,
			section_asset_size: false,
			section_plan_participants: false,
			section_state: false,
			section_zip_code: false,
		};
		this.selectedCampaign = campaign.id ? campaign.id : 0;

		this.searchProspects();
	}

	setCampaignModal(campaign: any){
		this.campaignToAssign = campaign.id;
	}

		criteriaSelected(criteria) {
			switch (criteria) {
				case 'employee_size':
					this.criterias.section_employee_size = !this.criterias.section_employee_size;
					if (this.criterias.section_employee_size) {
						this.formCriteria.get('employee_size_min').addValidators(Validators.required);
					} else {
						this.formCriteria.get('employee_size_min').removeValidators(Validators.required);
					}
					break;

				case 'asset_size':
					this.criterias.section_asset_size = !this.criterias.section_asset_size;
					if (this.criterias.section_asset_size) {
						this.formCriteria.get('asset_size_min').addValidators(Validators.required);
					} else {
						this.formCriteria.get('asset_size_min').removeValidators(Validators.required);
					}
					break;

				case 'plan_participants':
					this.criterias.section_plan_participants = !this.criterias.section_plan_participants;
					if (this.criterias.section_plan_participants) {
						this.formCriteria.get('plan_participants_min').addValidators(Validators.required);
					} else {
						this.formCriteria.get('plan_participants_min').removeValidators(Validators.required);
					}
					break;

				case 'state':
					this.criterias.section_state = !this.criterias.section_state;
					if (this.criterias.section_state) {
						this.formCriteria.get('state').addValidators(Validators.required);
					} else {
						this.formCriteria.get('state').removeValidators(Validators.required);
					}
					break;

				case 'zip_code':
					this.criterias.section_zip_code= !this.criterias.section_zip_code;
					if (this.criterias.section_zip_code) {
						this.formCriteria.get('zip_code').addValidators([Validators.required, Validators.maxLength(5)]);
						this.formCriteria.get('radius').addValidators(Validators.required);
					} else {
						this.formCriteria.get('zip_code').removeValidators([Validators.required, Validators.maxLength(5)]);
						this.formCriteria.get('radius').removeValidators(Validators.required);
					}
					break;
			}
		}

		setStatesFromArray(statesArray: { name: string, shortName: string }[]) {
			this.criterias.state = statesArray.map(state => state.shortName).join(', ');
		}

		/**
		 * Transforms the criteria object into a formatted string
		 * @param criteria - The criteria object to transform
		 * @returns A formatted string representation of the criteria
		 */
		transformCriteriaToString(criteria: CampaignCriteria): string {
			const parts: string[] = [];

			const criteriaState = criteria.state.replace(/\s/g, '');

			// State section
			if (criteria.section_state && criteriaState) {
				parts.push(`(state=${criteriaState})`);
			}

			// Zip code section
			if (criteria.section_zip_code && criteria.zip_code) {
				parts.push(`(zip=${criteria.zip_code})`);
			}

			// Employee size section
			if (criteria.section_employee_size && (criteria.employee_size_min > 0 || criteria.employee_size_max > 0)) {
				const minEmployee = criteria.employee_size_min || 0;
				const maxEmployee = criteria.employee_size_max || 0;

				if (minEmployee > 0 && maxEmployee > 0) {
					parts.push(`(employees=${minEmployee}-${maxEmployee})`);
				} else if (minEmployee > 0) {
					parts.push(`(employees=${minEmployee}+)`);
				} else if (maxEmployee > 0) {
					parts.push(`(employees=0-${maxEmployee})`);
				}
			}

			// Plan assets section
			if (criteria.section_asset_size && (criteria.asset_size_min > 0 || criteria.asset_size_max > 0)) {
				const minAsset = criteria.asset_size_min || 0;
				const maxAsset = criteria.asset_size_max || 0;

				if (minAsset > 0 && maxAsset > 0) {
					parts.push(`(assets=${minAsset}-${maxAsset})`);
				} else if (minAsset > 0) {
					parts.push(`(assets=${minAsset}+)`);
				} else if (maxAsset > 0) {
					parts.push(`(assets=0-${maxAsset})`);
				}
			}

			// Plan participants section
			if (criteria.section_plan_participants && (criteria.plan_participants_min > 0 || criteria.plan_participants_max > 0)) {
				const minParticipants = criteria.plan_participants_min || 0;
				const maxParticipants = criteria.plan_participants_max || 0;

				if (minParticipants > 0 && maxParticipants > 0) {
					parts.push(`(participants=${minParticipants}-${maxParticipants})`);
				} else if (minParticipants > 0) {
					parts.push(`(participants=${minParticipants}+)`);
				} else if (maxParticipants > 0) {
					parts.push(`(participants=0-${maxParticipants})`);
				}
			}

			return parts.join('');
		}


}
